import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "You can sanity-check a stock's value in five minutes using three ratios: P/E (price ÷ earnings), P/B (price ÷ book value), and PEG (P/E ÷ growth rate). Compare each to the stock's own history and to its direct competitors. If all three flag the stock as expensive without strong growth to justify it, look elsewhere. This isn't a full DCF — it's a fast first filter.";

const FAQS = [
  { q: 'Why can\'t I just use the P/E ratio alone?', a: 'P/E ignores growth and balance sheet quality. A 50x P/E is fine for a company growing 40%; a 20x P/E may be expensive for one growing 5%. Always layer in another ratio for context.' },
  { q: 'What is the PEG ratio?', a: 'PEG = P/E ÷ expected annual earnings growth (in percent). A PEG of 1 is fair value. Below 1 is potentially cheap; above 2 is expensive. Made famous by Peter Lynch.' },
  { q: 'What\'s a discounted cash flow (DCF)?', a: 'A DCF estimates the present value of a company\'s future cash flows. It\'s the most thorough valuation method, but small input changes lead to big output changes. The 5-minute checklist gets you 80% of the value with 5% of the effort.' },
  { q: 'How important is comparing to competitors?', a: 'Critical. A stock\'s P/E in isolation is meaningless. Comparing it to two or three direct peers tells you whether the market sees this company as better, worse, or just average.' },
  { q: 'What if a stock looks cheap on every metric?', a: 'It\'s either undervalued or there\'s something wrong. Cheap-looking stocks often deserve to be cheap (declining business, hidden debt, regulatory risk). Investigate the bear case before buying.' },
];

export default function HowToValueAStockPage() {
  return (
    <BlogArticleLayout
      slug="how-to-value-a-stock"
      title="How to Value a Stock in 5 Minutes (P/E, P/B, PEG)"
      description="You won't build a DCF in 5 minutes, but you can sanity-check a stock with three quick ratios. Here is the workflow."
      keywords="how to value a stock, stock valuation, PE ratio PB ratio PEG, stock valuation for beginners, how to value stocks quickly, fundamental analysis basics"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-pe-ratio', 'what-is-eps', 'stock-picking']}
    >
      <section>
        <h2>Why a 5-minute valuation framework?</h2>
        <p>
          A full discounted cash flow takes hours and depends heavily on assumptions. A 5-minute valuation is a first filter:
          enough to decide whether a stock deserves the deeper analysis or whether you should move on. Used consistently, it saves you days of research on companies that aren't worth the time.
        </p>
      </section>

      <section>
        <h2>Step 1: P/E ratio (price ÷ earnings)</h2>
        <p>
          Start with <Link to="/blog/what-is-pe-ratio">P/E</Link>. Look up the trailing P/E and compare it to:
        </p>
        <ul>
          <li>The stock's own 5-year average P/E.</li>
          <li>Two or three direct competitors.</li>
          <li>The relevant index (S&P 500 ~20–22).</li>
        </ul>
        <p>
          Worked example: Microsoft P/E is 35. The S&P 500 is at 22. Microsoft's own 5-year average is around 33. Verdict: roughly fair value, modestly expensive.
        </p>
      </section>

      <section>
        <h2>Step 2: P/B ratio (price ÷ book value)</h2>
        <p>
          Price-to-book divides market cap by shareholder equity. It tells you how much you're paying above the company's accounting value.
        </p>
        <ul>
          <li><strong>Below 1</strong> — trading below book. Often a value play, sometimes a warning.</li>
          <li><strong>1–3</strong> — typical for most industries.</li>
          <li><strong>Above 5</strong> — premium pricing. Usually justified only by high <Link to="/blog/what-is-roe">ROE</Link> or strong growth.</li>
        </ul>
        <p>
          Asset-light businesses (software, consulting) routinely trade at high P/B because their value isn't on the balance sheet. Banks and industrials are best compared on P/B.
        </p>
      </section>

      <section>
        <h2>Step 3: PEG ratio (P/E ÷ growth rate)</h2>
        <p>
          PEG fixes the biggest blind spot of P/E: growth. Formula: <strong>PEG = P/E ÷ Annual EPS Growth Rate</strong>.
        </p>
        <ul>
          <li><strong>PEG &lt; 1</strong> — potentially undervalued for the growth profile.</li>
          <li><strong>PEG ~ 1</strong> — fair value.</li>
          <li><strong>PEG &gt; 2</strong> — expensive. Growth has to massively exceed estimates to justify the price.</li>
        </ul>
        <p>
          Worked example: a stock with P/E 30 and forecast earnings growth of 25% has PEG = 30 ÷ 25 = 1.2. Slightly expensive but not unreasonable.
        </p>
      </section>

      <section>
        <h2>How do you put it all together?</h2>
        <p>
          A simple decision matrix:
        </p>
        <ul>
          <li><strong>All three flag cheap</strong> — interesting. Investigate why before buying.</li>
          <li><strong>All three flag expensive without strong growth</strong> — pass.</li>
          <li><strong>Mixed</strong> — most stocks live here. Decide based on which metric matters most for the sector (P/B for banks, PEG for growth, P/E for everything else).</li>
        </ul>
        <p>
          Cross-check with a quick read of the <Link to="/blog/how-to-read-a-balance-sheet">balance sheet</Link> for hidden debt and the <Link to="/blog/what-is-free-cash-flow">free cash flow</Link> trend. Then move on or dig deeper.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
